import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { TrendUp as TrendingUp, Info } from 'phosphor-react';
import { inToMm, mmToIn } from '../utils/units';

export const BridgeCamberInput = ({ camber, onCamberChange, units = 'imperial' }) => {
  const [inputValue, setInputValue] = useState(camber.toString());
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (units === 'metric') setInputValue(Math.round(inToMm(camber)).toString());
    else setInputValue(camber.toString());
  }, [camber, units]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return setIsValid(false);
    if (units === 'imperial') {
      if (numValue < -12 || numValue > 24) setIsValid(false);
      else { setIsValid(true); onCamberChange(numValue); }
    } else {
      const min = -12 * 25.4; const max = 24 * 25.4;
      if (numValue < min || numValue > max) setIsValid(false);
      else { setIsValid(true); onCamberChange(mmToIn(numValue)); }
    }
  };

  const sliderValue = units === 'metric' ? (parseFloat(inputValue) || 0) : (parseFloat(inputValue) || 0);
  const presetsIn = [0, 2, 4, 6, 8, 12];
  const presetsMm = presetsIn.map((i) => Math.round(inToMm(i)));

  return (
    <Card className="border-slate-600 bg-slate-800 shadow-xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-slate-100 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-400" />
          Bridge Camber
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="camber" className="text-slate-300">{units === 'metric' ? 'Camber Height (mm)' : 'Camber Height (inches)'}</Label>
          <Input id="camber" type="number" step={units === 'metric' ? '1' : '0.5'} value={inputValue} onChange={handleInputChange}
            className={`bg-slate-700 border-slate-600 text-slate-200 ${!isValid ? 'border-red-500 ring-red-500' : 'focus:border-blue-400 focus:ring-blue-400'}`}
            placeholder={units === 'metric' ? 'Enter camber in millimeters' : 'Enter camber in inches'}
            min={units === 'metric' ? Math.round(-12 * 25.4) : -12}
            max={units === 'metric' ? Math.round(24 * 25.4) : 24}
          />
          {!isValid && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <Info className="w-4 h-4" />
              {units === 'metric' ? 'Camber must be between -305 and 610 mm' : 'Camber must be between -12 and 24 inches'}
            </div>
          )}
          {isValid && (
            <div className="text-slate-400 text-sm">
              {(() => {
                const valIn = units === 'metric' ? mmToIn(parseFloat(inputValue) || 0) : (parseFloat(inputValue) || 0);
                if (valIn === 0) return 'Flat bridge (no camber)';
                const dir = valIn > 0 ? 'arch upward' : 'arch downward';
                return units === 'metric' ? `${Math.round(inToMm(valIn))} mm ${dir}` : `${valIn}" ${dir}`;
              })()}
            </div>
          )}
        </div>
        <div>
          <Label className="text-slate-300 text-sm mb-2 block">Visual Adjustment</Label>
          <Slider
            value={[units === 'metric' ? (parseFloat(inputValue) || 0) : (parseFloat(inputValue) || 0)]}
            onValueChange={(val) => {
              const v = val[0];
              setInputValue(String(v));
              if (units === 'metric') onCamberChange(mmToIn(v)); else onCamberChange(v);
              setIsValid(true);
            }}
            min={units === 'metric' ? Math.round(-12 * 25.4) : -12}
            max={units === 'metric' ? Math.round(24 * 25.4) : 24}
            step={units === 'metric' ? 5 : 0.5}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>{units === 'metric' ? `${Math.round(-12 * 25.4)} mm` : '-12"'}</span>
            <span>{units === 'metric' ? '0 mm' : '0"'}</span>
            <span>{units === 'metric' ? `${Math.round(24 * 25.4)} mm` : '24"'}</span>
          </div>
        </div>
        <div>
          <Label className="text-slate-300 text-sm mb-2 block">Quick Select ({units === 'metric' ? 'mm' : 'inches'})</Label>
          <div className="grid grid-cols-3 gap-2">
            {(units === 'metric' ? presetsMm : presetsIn).map((preset, idx) => (
              <Button key={idx} variant="outline" size="sm"
                className={`h-8 border-slate-600 text-slate-300 hover:bg-slate-600 ${
                  (units === 'metric' ? Math.round(inToMm(camber)) === preset : camber === preset) ? 'bg-blue-600 text-white border-blue-500' : 'bg-slate-700'
                }`}
                onClick={() => {
                  if (units === 'metric') onCamberChange(mmToIn(preset)); else onCamberChange(preset);
                  setIsValid(true);
                }}>
                {units === 'metric' ? `${preset}` : `${preset}"`}
              </Button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};